// Copyright (c) StrangeBee 2023

import React, { useEffect, useState, ReactElement } from 'react';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { classList } from '@Helpers/className';
import { useLocalMessage } from '@Helpers/locale';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { THTextArea } from '@Molecules/Inputs/THTextArea/THTextArea';
import useValidateForm from '@Hooks/useValidateForm';
import moment from 'moment';

import './TimelineDrawer.scss';

interface TimelineDrawerProps {
    data?: Record<string, any>;
    mode?: 'edit' | 'add';
    triggerElem?: ReactElement;
    onSubmit: (state: any) => void;
    className?: string;
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

const nameKey = {
    date: ['date'],
    endDate: ['endDate'],
    title: ['title'],
    description: ['description'],
};

const TimelineDrawer = ({
    data,
    className,
    onSubmit,
    triggerElem,
    mode = 'add',
    visible,
    onVisibleChange,
}: TimelineDrawerProps): ReactElement => {
    const local = useLocalMessage();
    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();
    const dataCurrent = data;
    const [initialValues, setInitialeValues] = useState<Record<string, any>>({});

    const computeOnFinishHandler = (toggleDrawer: any) => (state: any) => {
        toggleDrawer();
        onSubmit(state);
        setInitialeValues(mode == 'add' ? {} : state);
        onResetForm();
    };

    const onResetForm = () => {
        setFieldsTouched(false);
        form.resetFields();
    };

    useEffect(() => {
        form.setFieldsValue(initialValues);
    }, [initialValues]); //eslint-disable-line

    useEffect(() => {
        setInitialeValues(dataCurrent || {});
    }, [dataCurrent]); //eslint-disable-line

    return (
        <THDrawer
            title={
                mode === 'edit'
                    ? local('timeline.drawer.title-edit', { name: dataCurrent?.name || '' })
                    : local('timeline.drawer.title-add')
            }
            className={classList('timeline__drawer', className)}
            triggerElem={triggerElem}
            disableClose={fieldsTouched}
            visible={visible}
            afterVisibleChange={(visible: boolean) => {
                onVisibleChange?.(visible);
            }}
        >
            {(toggleDrawer) => (
                <THForm
                    autoComplete="off"
                    onFinish={computeOnFinishHandler(toggleDrawer)}
                    onValuesChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    form={form}
                    initialValues={initialValues}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                disabled={!isValid || !fieldsTouched}
                                onCancel={() => {
                                    onResetForm();
                                    toggleDrawer();
                                }}
                                footerText={local(
                                    mode === 'edit' ? 'timeline.action.confirmeEdit' : 'timeline.action.confirmeAdd',
                                )}
                            />
                        }
                    >
                        <THForm.Item
                            name={nameKey.title}
                            label={local('timeline.field.title')}
                            rules={[{ required: true, message: local('timeline.required.general') }]}
                        >
                            <THInput />
                        </THForm.Item>

                        <THForm.Item
                            name={nameKey.description}
                            label={local('timeline.field.description')}
                            rules={[{ required: true, message: local('timeline.required.general') }]}
                        >
                            <THTextArea />
                        </THForm.Item>

                        <THForm.Item
                            name={nameKey.date}
                            label={local('timeline.field.date')}
                            rules={[{ required: true, message: local('timeline.required.general') }]}
                        >
                            <THDatePicker showTime />
                        </THForm.Item>

                        <THForm.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => (
                                <THForm.Item
                                    name={nameKey.endDate}
                                    label={local('timeline.field.endDate')}
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                const isAfter = moment(value).isAfter(getFieldValue(nameKey.date));
                                                if (value && !isAfter) {
                                                    return Promise.reject(local('timeline.validator.earlier'));
                                                }

                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                >
                                    <THDatePicker
                                        disabledDate={(date) => date.valueOf() < getFieldValue(nameKey.date)}
                                        showTime
                                    />
                                </THForm.Item>
                            )}
                        </THForm.Item>
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};

export default TimelineDrawer;
